import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, getDocs, doc, setDoc } from 'firebase/firestore';

export const pdfActivityService = {
  // Upload PDF to Firebase Storage
  uploadActivityPDF: async (file, userId) => {
    try {
      const storageRef = ref(storage, `daily-activities/${userId}/${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        filename: file.name,
        uploadedAt: new Date()
      };
    } catch (error) {
      console.error('Error uploading PDF:', error);
      throw error;
    }
  },

  // Extract text from PDF using backend API (returns pages too)
  extractPDFText: async (pdfFile) => {
    try {
      // Check file size before uploading (100MB limit)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (pdfFile.size > maxSize) {
        throw new Error(`File too large. Maximum size is 100MB. Your file is ${(pdfFile.size / 1024 / 1024).toFixed(2)}MB`);
      }

      const formData = new FormData();
      formData.append('pdf', pdfFile);

      // Use full backend URL since we're serving the built app
      const apiUrl = 'http://localhost:3001/api/extract-pdf-text';

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || 'Failed to extract PDF text';
        throw new Error(errorMessage);
      }

      const result = await response.json();
      // Return both raw text and pages
      return { text: result.text, pages: result.pages, numPages: result.numPages };
    } catch (error) {
      console.error('Error extracting PDF text:', error);
      throw error;
    }
  },

  // Extract PDF pages as images
  extractPDFPagesAsImages: async (pdfFile) => {
    try {
      console.log('🖼️ Extracting PDF pages as images...');
      
      // Validate file
      if (!pdfFile) {
        throw new Error('No PDF file provided');
      }

      if (pdfFile.type !== 'application/pdf') {
        throw new Error('File must be a PDF');
      }

      // Check file size (100MB limit)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (pdfFile.size > maxSize) {
        throw new Error(`File too large. Maximum size is 100MB. Your file is ${(pdfFile.size / 1024 / 1024).toFixed(2)}MB`);
      }

      const formData = new FormData();
      formData.append('pdf', pdfFile);

      // Call the backend endpoint
      const response = await fetch('http://localhost:3001/api/extract-pdf-pages-as-images', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || 'Failed to extract PDF pages as images';
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ PDF pages extracted as images:', result);
      
      return result;
    } catch (error) {
      console.error('❌ Error extracting PDF pages as images:', error);
      throw error;
    }
  },

  // Parse numbered activities from PDF text (legacy)
  parseNumberedActivities: (pdfText) => {
    try {
      console.log('📄 Parsing PDF text for numbered activities...');
      
      // Split text into lines and look for numbered activities
      const lines = pdfText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      
      const activities = [];
      let currentActivity = null;
      let activityNumber = 0;
      
      // First pass: identify potential main activities
      const potentialActivities = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        // Look for main activity patterns
        const mainPattern = /^(\d+)[\.\)\:]\s*([A-Z][^.!?]*[.!?]?)$/i;
        const activityPattern = /^Activity\s+(\d+)[\.\)\:]\s*(.+)$/i;
        const exercisePattern = /^Exercise\s+(\d+)[\.\)\:]\s*(.+)$/i;
        
        const mainMatch = line.match(mainPattern);
        const activityMatch = line.match(activityPattern);
        const exerciseMatch = line.match(exercisePattern);
        
        const match = mainMatch || activityMatch || exerciseMatch;
        
        if (match) {
          const number = parseInt(match[1]);
          const title = match[2].trim();
          
          // Validation rules to filter out non-activities
          if (title.length < 15) continue; // Skip very short titles
          const stepKeywords = ['step', 'prerequisite', 'page', 'section', 'part', 'sub'];
          if (stepKeywords.some(keyword => title.toLowerCase().includes(keyword))) continue;
          if (title.split(' ').length < 3) continue;
          
          potentialActivities.push({
            lineIndex: i,
            number: number,
            title: title,
            line: line
          });
        }
      }
      
      console.log(`🔍 Found ${potentialActivities.length} potential activities`);
      
      // Second pass: group content for each activity
      for (let i = 0; i < potentialActivities.length; i++) {
        const activity = potentialActivities[i];
        const nextActivity = potentialActivities[i + 1];
        
        // Determine the end of this activity's content
        const startIndex = activity.lineIndex;
        const endIndex = nextActivity ? nextActivity.lineIndex : lines.length;
        
        // Collect all content for this activity
        const content = [];
        for (let j = startIndex; j < endIndex; j++) {
          content.push(lines[j]);
        }
        
        // Create the activity object
        const activityObj = {
          number: activity.number,
          title: activity.title,
          description: content.slice(1).join(' ').substring(0, 500) + '...', // First 500 chars of description
          content: content,
          type: 'daily_activity',
          isActive: true
        };
        
        activities.push(activityObj);
      }
      
      console.log(`✅ Parsed ${activities.length} numbered activities from PDF`);
      return activities;
    } catch (error) {
      console.error('Error parsing numbered activities:', error);
      throw error;
    }
  },

  // New: Parse activities by page pairing (every 2 pages = 1 activity)
  parseActivitiesByPages: (pages) => {
    try {
      const activities = [];
      const total = pages.length;
      
      for (let i = 0; i < total; i++) {
        const currentPage = pages[i] || '';
        const nextPage = pages[i + 1] || '';
        
        // Special case: Activity 3 spans 2 pages
        let pageContent = currentPage;
        let skipNext = false;
        
        // Check if this is activity 3 (spans 2 pages)
        const activity3Pattern = /Activity\s+3|^3\s*[\.\)\:\-]/i;
        if (activity3Pattern.test(currentPage) && nextPage) {
          pageContent = currentPage + '\n' + nextPage;
          skipNext = true; // Skip the next page since it's part of activity 3
        }
        
        // Extract activity number and title from the page
        const lines = pageContent.split('\n').map(l => l.trim()).filter(l => l);
        const topLines = lines.slice(0, 10); // Check first 10 lines
        
        let number = null;
        let title = null;
        
        // Look for activity number and title patterns
        for (const line of topLines) {
          // Pattern 1: "Activity X: Title"
          const activityMatch = line.match(/^Activity\s+(\d{1,3})[\.\)\:]?\s*(.+)?$/i);
          if (activityMatch) {
            number = parseInt(activityMatch[1]);
            title = (activityMatch[2] || '').trim();
            break;
          }
          
          // Pattern 2: "X. Title" or "X) Title"
          const numberMatch = line.match(/^(\d{1,3})\s*[\.\)\:\-]\s*(.+)?$/);
          if (numberMatch) {
            number = parseInt(numberMatch[1]);
            title = (numberMatch[2] || '').trim();
            break;
          }
          
          // Pattern 3: "X Title" (number followed by title)
          const simpleMatch = line.match(/^(\d{1,3})\s+(.{6,})$/);
          if (simpleMatch) {
            number = parseInt(simpleMatch[1]);
            title = simpleMatch[2].trim();
            break;
          }
        }
        
        // If no number found, use page index + 1
        if (!number) {
          number = i + 1;
        }
        
        // If no title found, use the first substantial line
        if (!title) {
          const substantialLines = topLines.filter(l => l.length > 10 && !l.match(/^\d+$/));
          title = substantialLines[0] || `Activity ${number}`;
        }
        
        // Create activity object
        activities.push({
          number: number,
          title: title,
          description: pageContent.substring(0, 600) + '...',
          content: pageContent.split('\n'),
          type: 'daily_activity',
          isActive: true
        });
        
        // Skip next page if it was part of activity 3
        if (skipNext) {
          i++; // Skip the next iteration
        }
      }
      
      console.log(`✅ Parsed ${activities.length} activities from ${total} pages`);
      return activities;
    } catch (error) {
      console.error('Error parsing activities by pages:', error);
      throw error;
    }
  },

  // Extract and upload activities from PDF
  extractAndUploadActivities: async (pdfFile, userId) => {
    try {
      console.log('🔄 Starting PDF activity extraction and upload...');
      
      // Step 1: Extract text from PDF (now returns pages)
      const { text, pages, numPages } = await pdfActivityService.extractPDFText(pdfFile);
      console.log('📄 PDF extracted:', { numPages: numPages || (pages ? pages.length : 'unknown') });
      
      // Step 2: Prefer page-based parsing to get 1 activity per 2 pages
      let activities = [];
      if (pages && pages.length) {
        activities = pdfActivityService.parseActivitiesByPages(pages);
      } else {
        activities = pdfActivityService.parseNumberedActivities(text);
      }
      console.log('📋 Activities parsed:', activities.length);
      
      // Step 3: Upload activities to Firestore
      const uploadResult = await pdfActivityService.storeActivitiesInFirestore(activities);
      console.log('✅ Activities uploaded to Firestore:', uploadResult);
      
      // Step 4: Upload PDF to storage
      const pdfUploadResult = await pdfActivityService.uploadActivityPDF(pdfFile, userId);
      console.log('📁 PDF uploaded to storage:', pdfUploadResult);
      
      return {
        success: true,
        activitiesCount: activities.length,
        activities: activities,
        pdfUrl: pdfUploadResult.url,
        message: `Successfully extracted and uploaded ${activities.length} activities from PDF`
      };
    } catch (error) {
      console.error('Error in extractAndUploadActivities:', error);
      throw error;
    }
  },

  // Test Firebase connection via backend
  testFirebaseConnection: async () => {
    try {
      console.log('🧪 Testing Firebase connection via backend...');
      
      // Test the backend endpoint
      const testActivities = [{
        title: 'Test Activity',
        number: 999,
        type: 'test',
        description: 'Test activity for connection verification'
      }];
      
      // Use full backend URL since we're serving the built app
      const apiUrl = 'http://localhost:3001/api/store-activities';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ activities: testActivities })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Backend test failed');
      }

      const result = await response.json();
      console.log('✅ Backend test successful:', result);
      
      return { success: true, message: 'Firebase connection test passed via backend' };
    } catch (error) {
      console.error('❌ Firebase connection test failed:', error);
      throw error;
    }
  },

  // Store activities in Firestore via backend (bypasses client-side rules)
  storeActivitiesInFirestore: async (activities) => {
    try {
      console.log('📤 Storing activities via backend...');
      console.log('📊 Activities to store:', activities.length);
      
      // Use full backend URL since we're serving the built app
      const apiUrl = 'http://localhost:3001/api/store-activities';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ activities })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || 'Failed to store activities';
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Successfully stored activities via backend:', result);
      return result;
    } catch (error) {
      console.error('❌ Error storing activities via backend:', error);
      throw error;
    }
  },

  // Get activities from Firestore
  getActivitiesFromFirestore: async () => {
    try {
      console.log('📖 Getting activities via backend...');
      
      // Use backend endpoint to bypass client-side rules
      const response = await fetch('http://localhost:3001/api/get-activities', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || 'Failed to get activities';
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Successfully retrieved activities via backend:', result.activities.length);
      
      return result.activities;
    } catch (error) {
      console.error('Error getting activities from Firestore:', error);
      throw error;
    }
  },

  // Admin function to update activities
  updateActivity: async (activityId, updates) => {
    try {
      const { db } = await import('../firebase');
      const { doc, setDoc } = await import('firebase/firestore');
      const activityRef = doc(db, 'dailyActivities', activityId);
      await setDoc(activityRef, {
        ...updates,
        updatedAt: new Date()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating activity:', error);
      throw error;
    }
  },

  // Delete activity
  deleteActivity: async (activityId) => {
    try {
      const { db } = await import('../firebase');
      const { doc, setDoc } = await import('firebase/firestore');
      const activityRef = doc(db, 'dailyActivities', activityId);
      await setDoc(activityRef, {
        isActive: false,
        deletedAt: new Date()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting activity:', error);
      throw error;
    }
  }
};

export default pdfActivityService;
